'use client';

import { GeoPointType, ProductType } from '@/@types';
import { UIComponent } from '@/components/core/components';
import { distanceBetweenGeopoints } from '@/utils/geo';
import { useLingui } from '@lingui/react';
import { Avatar, Button, Icon } from '@/components/core';
import useBookmark from '@/components/core/hooks/use-bookmark';
import { useAddCollectionContext } from '@/components/context/add-to-collection';
import InternalLink from '@/components/core/components/organism/internal-link';
import { subcategories } from '@/@constants';
import { msg } from '@lingui/macro';
import { useLocalizedLinks } from '@/components/core/hooks';
import { useUser } from '@/components/context/user';
import clsx from 'clsx';
import { MouseEventHandler } from 'react';
import { isProductOfType } from '@/services/api/parsers/utils';
import { Image } from '@/components/core/components/atoms';
import * as styles from './place-details.styles';
import { useDownloadApp } from '../../../download-app-modal/download-app-modal';

const PlaceDetails: UIComponent<{ place?: ProductType; currentPosition?: GeoPointType; onClose?: MouseEventHandler<HTMLElement> }> = ({
  className,
  place: placeProp,
  currentPosition,
  onClose,
}) => {
  const { i18n, _ } = useLingui();
  const { isLogged } = useUser();
  const place = useBookmark(placeProp);
  const { addToCollection } = useAddCollectionContext();
  const links = useLocalizedLinks();
  const downloadApp = useDownloadApp();

  if (!place || !isProductOfType(place, ['restaurants', 'places'])) {
    return null;
  }
  let distance = currentPosition && distanceBetweenGeopoints(place.extra.location?.geo || place.geo!, currentPosition);
  let distanceUnit = 'meter';

  if (distance && distance > 1000) {
    distanceUnit = 'kilometer';
    distance /= 1000;
  }

  const firstComment = place.comments?.at(0);

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.image}>
        <Image src={place.image!} alt={place.name} />
        <Button
          variant="tertiary"
          className={styles.bookmark}
          onClick={() => {
            if (isLogged) {
              addToCollection(place);
              return;
            }
            downloadApp('bookmark');
          }}
        >
          <Icon name={place.isSaved ? 'bookmark-fill' : 'bookmark'} />
        </Button>
      </div>
      <div className={styles.title}>
        <h3 className={styles.name}>
          <InternalLink href={links.product(place)}>{place.name}</InternalLink>
        </h3>
        <p className={styles.distance}>
          {_(
            subcategories.restaurants.concat(subcategories.places).find((x) => {
              return x.id === place.subcategory;
            })?.title || msg`Others`,
          )}

          {distance && (
            <>
              {' · '}
              {new Intl.NumberFormat(i18n.locale, { style: 'unit', unit: distanceUnit, maximumFractionDigits: 0 }).format(distance)}
            </>
          )}
        </p>
      </div>
      <div className={styles.info}>
        {place.address && (
          <a
            className={styles.address}
            target="_blank"
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place.address)}`}
          >
            {place.address}
          </a>
        )}
        {firstComment && (
          <div>
            <div className="flex gap-4">
              <Avatar size={32} src={firstComment.user.avatar} level={firstComment.user.level?.name} />
              {firstComment.user.nickname}
            </div>
            {firstComment.message}
          </div>
        )}
      </div>
      {onClose && <Icon name="close" className={styles.close} onClick={onClose} />}
    </div>
  );
};

export default PlaceDetails;
